import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SquarePayment from '../components/SquarePayment';

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [bookingData, setBookingData] = useState(null);
  const [paymentOption, setPaymentOption] = useState('card'); // 'card' or 'boarding'

  useEffect(() => {
    if (location.state?.bookingData) {
      setBookingData(location.state.bookingData);
    } else {
      navigate('/');
    }
  }, [location.state, navigate]);

  const handleBookingComplete = (result) => {
    navigate('/order-complete', {
      state: {
        bookingRefId: result.bookingRefId,
        paymentResult: result.payment,
        bookingData: result.booking
      }
    });
  };

  const handleError = (error) => {
    console.error('Booking/Payment error:', error);
  };

  if (!bookingData) return null;

  const totalAmount = parseFloat(bookingData.final_fare || 0).toFixed(2);

  return (
    <main className="min-h-screen bg-[#0b132b] text-slate-100 font-body-main py-10 px-4 sm:px-6 lg:px-8">
      {/* Header Banner */}
      <div className="max-w-7xl mx-auto mb-10 text-center">
        <span className="text-xs font-bold uppercase tracking-widest text-blue-400 block mb-1">Final Step</span>
        <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight">Secure Payment & Reservation Confirmation</h1>
        <p className="text-slate-400 text-sm mt-2">All transactions are 100% secure, encrypted with bank-grade 256-bit SSL protection.</p>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left Column: Payment Options & Card Form */}
        <div className="lg:col-span-8 space-y-6">
          <section className="rounded-2xl bg-slate-900/85 border border-slate-700/80 p-6 sm:p-8 backdrop-blur-xl shadow-2xl">
            <div className="flex items-center gap-3 mb-8 pb-5 border-b border-slate-800">
              <div className="w-10 h-10 rounded-xl bg-blue-600/20 text-blue-400 border border-blue-500/30 flex items-center justify-center">
                <span className="material-symbols-outlined !text-2xl">account_balance_wallet</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white tracking-tight">Select Payment Preference</h2>
                <p className="text-xs text-slate-400">Choose between immediate card authorization or payment upon chauffeur arrival.</p>
              </div>
            </div>

            {/* Payment Mode Selector Tabs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {/* Credit Card Option */}
              <div 
                onClick={() => setPaymentOption('card')}
                className={`p-5 rounded-2xl cursor-pointer transition-all duration-200 border flex flex-col justify-between gap-4 ${
                  paymentOption === 'card' 
                    ? 'bg-slate-950/90 border-blue-500 ring-2 ring-blue-500/30 shadow-xl' 
                    : 'bg-slate-950/50 border-slate-800 hover:border-slate-700'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="w-11 h-11 rounded-xl bg-blue-600/20 text-blue-400 border border-blue-500/30 flex items-center justify-center">
                    <span className="material-symbols-outlined !text-2xl">credit_card</span>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentOption === 'card' ? 'border-blue-500 bg-blue-600' : 'border-slate-600'}`}>
                    {paymentOption === 'card' && <div className="w-2 h-2 rounded-full bg-white"></div>}
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-white text-sm">Credit / Corporate Card</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Secure payment processed via Square SDK</p>
                </div>
              </div>
              
              {/* Pay on Boarding Option */}
              <div 
                onClick={() => setPaymentOption('boarding')}
                className={`p-5 rounded-2xl cursor-pointer transition-all duration-200 border flex flex-col justify-between gap-4 ${
                  paymentOption === 'boarding' 
                    ? 'bg-slate-950/90 border-blue-500 ring-2 ring-blue-500/30 shadow-xl' 
                    : 'bg-slate-950/50 border-slate-800 hover:border-slate-700'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="w-11 h-11 rounded-xl bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center">
                    <span className="material-symbols-outlined !text-2xl">local_atm</span>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentOption === 'boarding' ? 'border-blue-500 bg-blue-600' : 'border-slate-600'}`}>
                    {paymentOption === 'boarding' && <div className="w-2 h-2 rounded-full bg-white"></div>}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-white text-sm">Pay on Boarding (Cash)</h3>
                    <span className="text-[10px] font-bold text-emerald-300 bg-emerald-500/20 border border-emerald-500/30 px-2 py-0.5 rounded-full">
                      Save 10%
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">Settle directly with your chauffeur in cash and save 10%</p>
                </div>
              </div>
            </div>

            {/* Payment Content Area */}
            {paymentOption === 'card' ? (
              <div className="rounded-2xl bg-slate-950/80 border border-slate-800 p-2 sm:p-4">
                <SquarePayment
                  bookingRefId={bookingData.booking_ref_id}
                  amount={totalAmount}
                  passengerId={bookingData.passenger_id}
                  paymentType={bookingData.paymentType || 'FULL'}
                  onSuccess={(paymentResult) => {
                    handleBookingComplete({
                      booking: bookingData,
                      payment: paymentResult,
                      bookingRefId: bookingData.booking_ref_id,
                      passengerId: bookingData.passenger_id
                    });
                  }}
                  onError={handleError}
                />
              </div>
            ) : (
              <div className="p-8 rounded-2xl bg-slate-950/80 border border-slate-800 text-center space-y-5">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 mx-auto flex items-center justify-center">
                  <span className="material-symbols-outlined !text-3xl">verified</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Guaranteed Chauffeur Reservation</h3>
                  <p className="text-sm text-slate-300 leading-relaxed max-w-md mx-auto mt-1">
                    Your executive vehicle will be dispatched according to your flight schedule. Pay your chauffeur directly upon completion of your trip.
                  </p>
                </div>
                <button 
                  onClick={() => {
                    handleBookingComplete({
                      booking: bookingData,
                      payment: { status: 'BOARDING' },
                      bookingRefId: bookingData.booking_ref_id,
                      passengerId: bookingData.passenger_id
                    });
                  }}
                  className="w-full py-4 rounded-xl exec-btn-primary flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-extrabold shadow-xl"
                >
                  <span className="material-symbols-outlined !text-base">check_circle</span>
                  Confirm Reservation & Pay On Boarding
                </button>
              </div>
            )}
            
            <p className="text-center mt-6 text-xs text-slate-500">
              By confirming this booking, you agree to our 24-Hour Free Cancellation Policy and Executive Terms of Service.
            </p>
          </section>
        </div>

        {/* Right Column: Order Summary */}
        <div className="lg:col-span-4 lg:sticky lg:top-28">
          <div className="rounded-2xl bg-slate-900/90 border border-slate-700/80 p-6 backdrop-blur-2xl shadow-2xl">
            <div className="flex items-center justify-between pb-4 border-b border-slate-800 mb-5">
              <h3 className="font-bold text-white text-base">Booking Summary</h3>
              <span className="text-[11px] font-mono text-blue-300 bg-blue-500/10 border border-blue-500/30 px-2.5 py-0.5 rounded-md">
                Ref: {bookingData.booking_ref_id || "PENDING"}
              </span>
            </div>
            
            <div className="space-y-4 text-xs">
              <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400">Pickup</span>
                  <p className="text-slate-200 font-medium line-clamp-2 mt-0.5">{bookingData.booking?.pickup_address || bookingData.start || 'Pickup location'}</p>
                </div>
                <div className="pt-2 border-t border-slate-800">
                  <span className="text-[10px] uppercase font-bold text-slate-400">Dropoff</span>
                  <p className="text-slate-200 font-medium line-clamp-2 mt-0.5">{bookingData.booking?.dropoff_address || bookingData.end || 'Destination'}</p>
                </div>
                {(Number(bookingData.booster_seat) > 0 || Number(bookingData.regular_seat) > 0 || Number(bookingData.infant_seat) > 0) && (
                  <div className="pt-2 border-t border-slate-800 flex items-center justify-between text-[11px]">
                    <span className="text-slate-400 flex items-center gap-1">
                      <span className="material-symbols-outlined text-emerald-400 !text-sm">child_care</span>
                      Child Seats:
                    </span>
                    <span className="text-emerald-300 font-medium">
                      {[
                        Number(bookingData.booster_seat) > 0 ? `${bookingData.booster_seat} Booster` : null,
                        Number(bookingData.regular_seat) > 0 ? `${bookingData.regular_seat} Regular` : null,
                        Number(bookingData.infant_seat) > 0 ? `${bookingData.infant_seat} Infant` : null
                      ].filter(Boolean).join(' • ')}
                    </span>
                  </div>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="pt-2 space-y-2.5 text-slate-400">
                <div className="flex justify-between">
                  <span>Chauffeur Ride Fare</span>
                  <span className="text-white font-mono font-medium">${totalAmount}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tolls & Logan Airport Access</span>
                  <span className="text-blue-300 font-medium">Included</span>
                </div>
                <div className="flex justify-between">
                  <span>Gratuity & Taxes</span>
                  <span className="text-blue-300 font-medium">Included</span>
                </div>
                
                {paymentOption === "boarding" ? (
                  <>
                    <div className="flex justify-between text-emerald-400 text-xs">
                      <span>Cash Discount (10% OFF)</span>
                      <span className="font-mono font-medium">-${(parseFloat(totalAmount) * 0.10).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-end pt-4 border-t border-slate-800 mt-3">
                      <div>
                        <span className="text-xs uppercase font-bold tracking-wider text-emerald-400">Total Cash Due</span>
                        <p className="text-[10px] text-slate-500">10% Cash discount applied</p>
                      </div>
                      <span className="text-3xl font-black text-emerald-400 font-mono">
                        ${(parseFloat(totalAmount) * 0.90).toFixed(2)}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between items-end pt-4 border-t border-slate-800 mt-3">
                    <div>
                      <span className="text-xs uppercase font-bold tracking-wider text-slate-300">Total Card Price</span>
                      <p className="text-[10px] text-slate-500">All fees & taxes included</p>
                    </div>
                    <span className="text-3xl font-black text-white font-mono">${totalAmount}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-6 pt-4 border-t border-slate-800 flex items-center justify-center gap-2 text-[11px] text-slate-400">
              <span className="material-symbols-outlined text-emerald-400 !text-sm">verified_user</span>
              <span>100% Secure & Encrypted Checkout</span>
            </div>
          </div>
        </div>

      </div>
    </main>
  );
};

export default PaymentPage;
